// Wer ist wann weg — das ganze Jahr auf einen Blick.
//
// ══ Warum es das braucht ══
//
// Die Anträge standen bisher nur als Liste da. Bei drei Leuten geht das noch;
// sobald sechs im Team sind, muss Sebastian sich die Zeiträume im Kopf
// übereinanderlegen, um zu sehen, ob zwei gleichzeitig fehlen. Genau daran
// scheitert eine Urlaubsplanung — nicht am einzelnen Antrag.
//
// Eine Zeile je Person, zwölf Monate nebeneinander, jeder Tag ein Strich:
//   durchgezogen grün = genehmigt
//   gestreift        = beantragt, noch nicht entschieden
// Abgelehnte und zurückgezogene tauchen nicht auf — sie sind keine Abwesenheit.
//
// Beantragt und genehmigt sehen bewusst UNTERSCHIEDLICH aus und nicht nur
// unterschiedlich farbig: Wer auf den Streifen schaut, soll auch bei Sonne oder
// mit müden Augen erkennen, was schon zugesagt ist.

import { useMemo, useState } from "react";
import { useAlleAbwesenheiten, type Abwesenheit } from "@/lib/urlaub";
import { LogoLoader } from "@/components/LogoLoader";
import { EmptyState, cn } from "@/design-system/ui";

const MONATE = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

/** Tag des Jahres (0-basiert) — die Position auf dem Streifen. */
function tagImJahr(iso: string) {
  const d = new Date(`${iso}T12:00:00`);
  const start = new Date(d.getFullYear(), 0, 1, 12);
  return Math.round((d.getTime() - start.getTime()) / 86_400_000);
}

const tageImJahr = (jahr: number) => (jahr % 4 === 0 && (jahr % 100 !== 0 || jahr % 400 === 0) ? 366 : 365);

export function UrlaubsJahr() {
  const [jahr, setJahr] = useState(new Date().getFullYear());
  const { data: alle = [], isLoading } = useAlleAbwesenheiten(jahr);

  // Nur was WIRKLICH eine Abwesenheit ist. Ein abgelehnter Antrag darf hier
  // nicht als Balken stehen — sonst plant jemand um einen Urlaub herum, den es
  // nicht gibt.
  const zaehlt = useMemo(
    () => alle.filter((a) => a.status === "beantragt" || a.status === "genehmigt"),
    [alle]
  );

  const proPerson = useMemo(() => {
    const map = new Map<
      string,
      { name: string; eintraege: Abwesenheit[]; anspruch: number; genommen: number; beantragt: number }
    >();
    for (const a of zaehlt) {
      const g = map.get(a.profile_id) ?? {
        name: a.person?.full_name ?? "Ohne Namen",
        eintraege: [],
        // 25 ist der Standard aus dem Team-Bereich. Steht dort etwas anderes,
        // gilt das — die Zahl wird NICHT hier festgelegt.
        anspruch: Number(a.person?.urlaubstage_jahr ?? 25),
        genommen: 0,
        beantragt: 0,
      };
      g.eintraege.push(a);

      // Nur echter Urlaub zählt aufs Konto. Krankheit und Sonstiges stehen zwar
      // im Streifen (man ist ja weg), zehren aber keine Urlaubstage auf — sie
      // abzuziehen wäre schlicht falsch gerechnet.
      if (a.art === "urlaub") {
        if (a.status === "genehmigt") g.genommen += Number(a.arbeitstage);
        else g.beantragt += Number(a.arbeitstage);
      }
      map.set(a.profile_id, g);
    }
    return [...map.values()].sort((x, y) => x.name.localeCompare(y.name, "de"));
  }, [zaehlt]);

  const tage = tageImJahr(jahr);
  const heute = new Date();
  const heuteMarke = heute.getFullYear() === jahr ? (tagImJahr(heute.toISOString().slice(0, 10)) / tage) * 100 : null;

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
      {/* Jahreswahl — schlicht, weil man selten wechselt. */}
      <div className="mb-4 flex items-center justify-between">
        <button
          onClick={() => setJahr((j) => j - 1)}
          className="h-9 rounded-lg px-3 text-sm text-ink-300 transition-colors duration-fast hover:bg-ink-800"
        >
          ‹ {jahr - 1}
        </button>
        <span className="text-[15px] font-semibold tabular-nums text-ink-50">{jahr}</span>
        <button
          onClick={() => setJahr((j) => j + 1)}
          className="h-9 rounded-lg px-3 text-sm text-ink-300 transition-colors duration-fast hover:bg-ink-800"
        >
          {jahr + 1} ›
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <LogoLoader size="md" label="Urlaube werden geladen" />
        </div>
      ) : proPerson.length === 0 ? (
        <EmptyState title="Keine Abwesenheiten" hint={`${jahr} hat noch niemand Urlaub beantragt.`} />
      ) : (
        <>
          {/* Monatsraster als Kopfzeile. Die Namensspalte ist fest, damit die
              Streifen bei allen an derselben Stelle beginnen. */}
          <div className="-mx-4 overflow-x-auto px-4 pb-1">
          <div className="min-w-[46rem]">
          <div className="mb-1 flex gap-2 pl-24 pr-[6.5rem]">
            {MONATE.map((m) => (
              <span key={m} className="flex-1 text-center text-[10px] text-ink-500">
                {m}
              </span>
            ))}
          </div>

          <ul className="space-y-2">
            {proPerson.map((p) => (
              <li key={p.name} className="flex items-center gap-2">
                <span className="w-22 shrink-0 truncate text-sm text-ink-200" style={{ width: "5.5rem" }}>
                  {p.name}
                </span>

                <div className="relative h-7 flex-1 overflow-hidden rounded-md bg-ink-800/70">
                  {/* Monatsgrenzen — ohne sie kann man einen Balken keinem
                      Zeitpunkt zuordnen. */}
                  {MONATE.map((_, i) => (
                    <span
                      key={i}
                      className="absolute top-0 h-full w-px bg-ink-700/60"
                      style={{ left: `${(tagImJahr(`${jahr}-${String(i + 1).padStart(2, "0")}-01`) / tage) * 100}%` }}
                      aria-hidden
                    />
                  ))}

                  {p.eintraege.map((a) => {
                    const von = tagImJahr(a.von);
                    const bis = tagImJahr(a.bis);
                    const links = (von / tage) * 100;
                    // Mindestens ein Prozent Breite: Ein einzelner Urlaubstag
                    // wäre sonst unsichtbar — und genau der wird gern übersehen.
                    const breite = Math.max(((bis - von + 1) / tage) * 100, 0.9);
                    const genehmigt = a.status === "genehmigt";
                    return (
                      <span
                        key={a.id}
                        title={`${a.person?.full_name ?? ""}: ${a.von} bis ${a.bis} · ${a.arbeitstage} Tage · ${genehmigt ? "genehmigt" : "beantragt"}`}
                        className={cn(
                          "absolute top-1 h-5 rounded-sm",
                          genehmigt ? "bg-gruen-500" : "border border-warn-fg/60"
                        )}
                        style={{
                          left: `${links}%`,
                          width: `${breite}%`,
                          // Gestreift = beantragt. Die Schraffur wirkt auch dann,
                          // wenn jemand Farben schlecht unterscheidet.
                          ...(genehmigt
                            ? {}
                            : {
                                backgroundImage:
                                  "repeating-linear-gradient(45deg, rgb(234 179 8 / .55) 0 3px, transparent 3px 6px)",
                              }),
                        }}
                      />
                    );
                  })}

                  {heuteMarke !== null && (
                    <span
                      className="absolute top-0 h-full w-px bg-ink-100/70"
                      style={{ left: `${heuteMarke}%` }}
                      aria-label="heute"
                    />
                  )}
                </div>

                {/* ══ Das Urlaubskonto am Zeilenende ══
                    Beantragte Tage stehen SEPARAT und werden nicht vom Rest
                    abgezogen — sonst sähe es aus, als wären sie schon vergeben.
                    Sie aber wegzulassen wäre schlimmer: Dann genehmigt man
                    jemandem seinen letzten Tag zweimal. */}
                <span
                  className="w-24 shrink-0 text-right text-xs tabular-nums"
                  title={`${p.anspruch} Tage Anspruch · ${p.genommen} genehmigt · ${p.beantragt} beantragt`}
                >
                  <span
                    className={cn(
                      "text-sm font-semibold",
                      p.anspruch - p.genommen - p.beantragt < 0 ? "text-danger-fg" : "text-ink-50"
                    )}
                  >
                    {p.anspruch - p.genommen}
                  </span>
                  <span className="text-ink-500"> / {p.anspruch}</span>
                  {p.beantragt > 0 && (
                    <span className="block text-warn-fg">+{p.beantragt} beantragt</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
          </div>
          </div>

          {/* Legende — ohne sie rätselt man, was gestreift bedeutet. */}
          <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-ink-400">
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-5 rounded-sm bg-gruen-500" /> genehmigt
            </span>
            <span className="flex items-center gap-1.5">
              <span
                className="h-3 w-5 rounded-sm border border-warn-fg/60"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(45deg, rgb(234 179 8 / .55) 0 3px, transparent 3px 6px)",
                }}
              />
              beantragt
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-px bg-ink-100/70" /> heute
            </span>
            <span className="text-ink-500">rechts: Resturlaub / Anspruch</span>
          </div>

          {/* Wer ist gleichzeitig weg? Die Frage, wegen der es diese Ansicht
              gibt — hier ausgerechnet statt dem Auge überlassen. */}
          <Ueberschneidungen eintraege={zaehlt} />
        </>
      )}
    </div>
  );
}

/**
 * Zeiträume, in denen sich mindestens zwei Abwesenheiten überlappen.
 *
 * Zwei Leute gleichzeitig weg ist bei drei Fahrern ein Problem — das soll nicht
 * erst auffallen, wenn der Dienstag vor der Tür steht.
 */
function Ueberschneidungen({ eintraege }: { eintraege: Abwesenheit[] }) {
  const treffer = useMemo(() => {
    const raus: { a: Abwesenheit; b: Abwesenheit }[] = [];
    for (let i = 0; i < eintraege.length; i++) {
      for (let j = i + 1; j < eintraege.length; j++) {
        const a = eintraege[i];
        const b = eintraege[j];
        if (a.profile_id === b.profile_id) continue;
        if (a.von <= b.bis && b.von <= a.bis) raus.push({ a, b });
      }
    }
    return raus;
  }, [eintraege]);

  if (!treffer.length) return null;

  return (
    <div className="mt-4 rounded-lg border border-warn-bg/30 bg-warn-bg/10 px-4 py-3">
      <p className="text-sm font-medium text-warn-fg">
        {treffer.length === 1 ? "Eine Überschneidung" : `${treffer.length} Überschneidungen`}
      </p>
      <ul className="mt-1 space-y-0.5">
        {treffer.slice(0, 5).map(({ a, b }, i) => (
          <li key={i} className="text-sm text-ink-300">
            {a.person?.full_name} und {b.person?.full_name} —{" "}
            <span className="tabular-nums">
              {a.von > b.von ? a.von : b.von} bis {a.bis < b.bis ? a.bis : b.bis}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
